import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";

export function PageSkeleton({ className }: { className?: string }) {
  return (
    <div
      className={cn(
        "w-full max-w-6xl mx-auto space-y-6 p-4 sm:p-6 animate-in fade-in duration-300",
        className,
      )}
    >
      <div className="space-y-3">
        <Skeleton className="h-8 w-40 sm:w-56 skeleton-shimmer" />
        <Skeleton className="h-4 w-full max-w-md skeleton-shimmer" />
      </div>
      <CardGridSkeleton />
      <TableSkeleton rows={6} />
    </div>
  );
}

export function DashboardSkeleton({ className }: { className?: string }) {
  return (
    <div className={cn("w-full space-y-6 p-3 sm:p-6", className)}>
      <Skeleton className="h-10 w-48 sm:w-72 skeleton-shimmer" />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div
            key={i}
            className="rounded-xl border border-border/60 bg-card/40 p-5 space-y-4"
          >
            <Skeleton className="h-4 w-24 skeleton-shimmer" />
            <Skeleton className="h-8 w-32 skeleton-shimmer" />
            <Skeleton className="h-16 w-full skeleton-shimmer" />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Skeleton className="h-40 w-full rounded-xl skeleton-shimmer" />
        <Skeleton className="h-40 w-full rounded-xl skeleton-shimmer" />
      </div>
    </div>
  );
}

export function CardGridSkeleton({ count = 4 }: { count?: number }) {
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4">
      {Array.from({ length: count }).map((_, i) => (
        <div
          key={i}
          className="rounded-xl border border-border/60 p-4 space-y-3"
        >
          <Skeleton className="h-10 w-10 rounded-full skeleton-shimmer" />
          <Skeleton className="h-6 w-20 skeleton-shimmer" />
          <Skeleton className="h-4 w-28 skeleton-shimmer" />
        </div>
      ))}
    </div>
  );
}

export function TableSkeleton({
  rows = 5,
  cols = 5,
  className,
}: {
  rows?: number;
  cols?: number;
  className?: string;
}) {
  return (
    <div
      className={cn(
        "rounded-xl border border-border/60 overflow-hidden",
        className,
      )}
    >
      <div className="flex gap-3 p-4 bg-muted/40">
        {Array.from({ length: cols }).map((_, i) => (
          <Skeleton key={i} className="h-4 flex-1 skeleton-shimmer" />
        ))}
      </div>
      <div className="divide-y divide-border/50">
        {Array.from({ length: rows }).map((_, row) => (
          <div key={row} className="flex gap-3 p-4">
            {Array.from({ length: cols }).map((_, col) => (
              <Skeleton
                key={col}
                className="h-5 flex-1 skeleton-shimmer"
                style={{ animationDelay: `${(row * cols + col) * 40}ms` }}
              />
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}

export function FormSkeleton() {
  return (
    <div className="space-y-4 w-full max-w-md mx-auto p-4">
      <Skeleton className="h-8 w-40 skeleton-shimmer" />
      {Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="space-y-2">
          <Skeleton className="h-4 w-24 skeleton-shimmer" />
          <Skeleton className="h-11 w-full skeleton-shimmer" />
        </div>
      ))}
      <Skeleton className="h-11 w-full skeleton-shimmer" />
    </div>
  );
}

export function TransferHistorySkeleton() {
  return (
    <div className="space-y-3">
      <div className="hidden md:block">
        <TableSkeleton rows={5} cols={5} />
      </div>
      <div className="md:hidden space-y-3">
        {Array.from({ length: 4 }).map((_, i) => (
          <div
            key={i}
            className="rounded-xl border border-border/60 p-4 space-y-3"
          >
            <div className="flex justify-between">
              <Skeleton className="h-4 w-24 skeleton-shimmer" />
              <Skeleton className="h-5 w-16 rounded-full skeleton-shimmer" />
            </div>
            <Skeleton className="h-5 w-32 skeleton-shimmer" />
            <Skeleton className="h-4 w-full skeleton-shimmer" />
          </div>
        ))}
      </div>
    </div>
  );
}
